import { forwardRef, type ButtonHTMLAttributes, type AnchorHTMLAttributes, type ReactNode } from 'react'

type Variant = 'primary' | 'accent' | 'ghost' | 'outline'
type Size = 'sm' | 'md' | 'lg'

interface BaseProps {
  variant?: Variant
  size?: Size
  iconRight?: ReactNode
  iconLeft?: ReactNode
  fullWidth?: boolean
  children: ReactNode
}

type ButtonProps = BaseProps & ButtonHTMLAttributes<HTMLButtonElement> & { as?: 'button' }
type LinkProps = BaseProps & AnchorHTMLAttributes<HTMLAnchorElement> & { as: 'a'; href: string }

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-primary text-surface hover:bg-primary-dark border-2 border-primary',
  accent:
    'bg-accent text-ink hover:bg-accent-soft border-2 border-accent',
  ghost:
    'bg-transparent text-ink hover:bg-surface-2 border-2 border-transparent',
  outline:
    'bg-transparent text-primary hover:bg-primary hover:text-surface border-2 border-primary',
}

const sizeClasses: Record<Size, string> = {
  sm: 'px-4 py-2 text-sm gap-2',
  md: 'px-6 py-3 text-base gap-2',
  lg: 'px-8 py-4 text-lg gap-3',
}

const baseClasses =
  'inline-flex items-center justify-center font-semibold rounded-yada-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-yada-md active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2'

/**
 * Button — supports button and anchor (via `as="a"` + `href`)
 */
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps | LinkProps>(
  ({ variant = 'primary', size = 'md', iconLeft, iconRight, fullWidth, children, className = '', ...rest }, ref) => {
    const classes = [
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      fullWidth ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    if ('as' in rest && rest.as === 'a') {
      const { as: _as, ...anchorRest } = rest as LinkProps
      return (
        <a ref={ref as React.Ref<HTMLAnchorElement>} className={classes} {...anchorRest}>
          {iconLeft && <span aria-hidden="true">{iconLeft}</span>}
          <span>{children}</span>
          {iconRight && <span aria-hidden="true">{iconRight}</span>}
        </a>
      )
    }

    const { as: _as, ...buttonRest } = rest as ButtonProps
    return (
      <button ref={ref as React.Ref<HTMLButtonElement>} className={classes} {...buttonRest}>
        {iconLeft && <span aria-hidden="true">{iconLeft}</span>}
        <span>{children}</span>
        {iconRight && <span aria-hidden="true">{iconRight}</span>}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
